import React from 'react';
import { Chrome, Download, ShieldCheck, Zap, RefreshCw, Lock, CheckCircle, ArrowRight, Sparkles, MousePointer2 } from 'lucide-react';
import { Card, Button } from '../components/ui';

const FeatureItem = ({ icon: Icon, title, description }) => (
    <div className="flex items-start space-x-4 p-5 rounded-xl bg-slate-50 dark:bg-slate-700/50 transition-all hover:bg-brand-50 dark:hover:bg-brand-900/20 group">
        <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-400 group-hover:scale-110 transition-transform">
            <Icon size={24} />
        </div>
        <div>
            <h3 className="font-semibold text-slate-800 dark:text-white mb-1">{title}</h3>
            <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">{description}</p>
        </div>
    </div>
);

const StepCard = ({ number, title, description }) => (
    <Card className="relative">
        <div className="absolute -top-4 -left-4 w-10 h-10 bg-brand-600 text-white rounded-full flex items-center justify-center font-bold text-lg shadow-md">
            {number}
        </div>
        <h3 className="font-bold text-slate-800 dark:text-white mb-2 mt-2">{title}</h3>
        <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">{description}</p>
    </Card>
);

export default function ExtensionPage({ navigate }) {
    const handleLinkClick = (e, path) => {
        e.preventDefault();
        navigate(path);
    };

    return (
        <div className="bg-slate-50 dark:bg-slate-900 transition-colors duration-200">
            {/* Hero Section */}
            <div className="relative overflow-hidden bg-gradient-to-br from-brand-700 via-brand-800 to-emerald-900 text-white">
                <div className="absolute inset-0 opacity-10 pointer-events-none">
                    <div className="absolute top-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 right-0 w-80 h-80 bg-brand-400 rounded-full blur-3xl"></div>
                </div>
                <div className="relative container mx-auto max-w-6xl px-4 py-20">
                    <div className="flex flex-col md:flex-row items-center gap-12">
                        <div className="flex-1 text-center md:text-left">
                            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-brand-200 text-sm font-medium mb-6">
                                <Sparkles size={16} className="mr-2" /> Free Chrome Extension
                            </div>
                            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                                Gift Scout for <span className="text-brand-300">Chrome</span>
                            </h1>
                            <p className="text-xl text-brand-100 mb-8 leading-relaxed max-w-xl">
                                Your gift cards, at your fingertips. Auto-fill codes at checkout, track balances on the go, and never miss a savings opportunity.
                            </p>
                            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center md:justify-start">
                                <Button
                                    as="a"
                                    href="https://chrome.google.com/webstore"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="secondary"
                                    pill
                                    size="xl"
                                    icon={Download}
                                    className="bg-white text-brand-800 hover:bg-brand-50 shadow-lg hover:shadow-xl hover:scale-105 transition-all border-none"
                                >
                                    Add to Chrome — Free
                                </Button>
                                <a
                                    href="/tool"
                                    onClick={(e) => handleLinkClick(e, '/tool')}
                                    className="inline-flex items-center text-brand-200 hover:text-white transition-colors font-medium"
                                >
                                    Try the Web App <ArrowRight className="ml-2 h-4 w-4" />
                                </a>
                            </div>
                            <div className="mt-8 flex items-center justify-center md:justify-start gap-6 text-brand-200 text-sm">
                                <span className="flex items-center"><CheckCircle size={16} className="mr-2" /> Works with 1000+ stores</span>
                                <span className="flex items-center"><Lock size={16} className="mr-2" /> End-to-end encrypted</span>
                            </div>
                        </div>
                        <div className="flex-shrink-0">
                            <img
                                src="/images/logo_with_smartphone.webp"
                                alt="Gift Scout Extension"
                                className="h-72 md:h-96 drop-shadow-2xl"
                                loading="eager"
                                width="384"
                                height="384"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Features Grid */}
            <div className="py-24 px-4">
                <div className="container mx-auto max-w-5xl">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-white mb-4">
                            Everything You Need at Checkout
                        </h2>
                        <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
                            The Gift Scout extension brings the power of your card vault right into your browser.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <FeatureItem
                            icon={MousePointer2}
                            title="One-Click Auto-Fill"
                            description="Gift Scout automatically detects gift card fields and fills in your code and PIN with a single click. No more copy-pasting!"
                        />
                        <FeatureItem
                            icon={RefreshCw}
                            title="Real-Time Balance Updates"
                            description="After you use a card, update the balance instantly. It syncs to your vault so you always know what's left."
                        />
                        <FeatureItem
                            icon={ShieldCheck}
                            title="Secure & Private"
                            description="Your cards are encrypted with your private PIN. We never see your data—it's decrypted only on your device."
                        />
                        <FeatureItem
                            icon={Zap}
                            title="Instant Access"
                            description="Click the Gift Scout icon to see all your cards for the current store. No searching, no hassle."
                        />
                        <FeatureItem
                            icon={Chrome}
                            title="Works Everywhere"
                            description="Compatible with Chrome, Edge, Brave, Opera, and other Chromium browsers. Use it wherever you shop."
                        />
                        <FeatureItem
                            icon={Lock}
                            title="PIN-Protected"
                            description="Your extension stays locked until you enter your PIN. Even if someone has your browser, your cards are safe."
                        />
                    </div>
                </div>
            </div>

            {/* How to Install */}
            <div className="py-24 px-4 bg-white dark:bg-slate-800/50">
                <div className="container mx-auto max-w-4xl">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-white mb-4">
                            Get Started in Minutes
                        </h2>
                        <p className="text-lg text-slate-600 dark:text-slate-400">
                            Three simple steps to unlock checkout superpowers.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        <StepCard
                            number="1"
                            title="Install the Extension"
                            description="Click 'Add to Chrome' and confirm the installation. Gift Scout will appear in your toolbar."
                        />
                        <StepCard
                            number="2"
                            title="Sign In & Enter PIN"
                            description="Use your Gift Scout account to sign in, then enter your secure PIN to unlock your cards."
                        />
                        <StepCard
                            number="3"
                            title="Shop & Save"
                            description="When you're at checkout, Gift Scout shows your available cards. Click to auto-fill and save!"
                        />
                    </div>
                </div>
            </div>

            {/* Browser Compatibility */}
            <div className="py-20 px-4">
                <div className="container mx-auto max-w-4xl">
                    <Card className="p-8 md:p-12">
                        <div className="flex flex-col md:flex-row items-center gap-8">
                            <div className="flex-shrink-0 flex items-center justify-center">
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                                    <Chrome size={32} className="text-white" />
                                </div>
                            </div>
                            <div className="flex-1 text-center md:text-left">
                                <h3 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">
                                    Built for Chrome & Chromium Browsers
                                </h3>
                                <p className="text-slate-600 dark:text-slate-300 mb-2">
                                    Gift Scout works with Google Chrome, Microsoft Edge, Brave, Opera, Vivaldi, and any Chromium-based browser.
                                </p>
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    Firefox and Safari support coming soon!
                                </p>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Final CTA */}
            <div className="py-20 px-4">
                <div className="container mx-auto max-w-4xl">
                    <div className="bg-gradient-to-br from-brand-700 via-brand-800 to-emerald-900 rounded-3xl shadow-card-prominent text-white relative overflow-hidden">
                        <div className="absolute inset-0 opacity-10 pointer-events-none">
                            <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
                            <div className="absolute bottom-10 right-10 w-64 h-64 bg-brand-400 rounded-full blur-3xl"></div>
                        </div>
                        <div className="relative z-10 p-12 md:p-16 text-center">
                            <img
                                src="/images/logo_standing.webp"
                                alt="Gift Scout Mascot"
                                className="h-28 mx-auto mb-6"
                                loading="lazy"
                                decoding="async"
                                width="112"
                                height="112"
                            />
                            <h2 className="text-3xl md:text-4xl font-bold mb-4">
                                Ready to Simplify Your Checkout?
                            </h2>
                            <p className="text-lg text-brand-100 mb-8 max-w-xl mx-auto">
                                Join smart shoppers using Gift Scout to manage their gift cards effortlessly.
                            </p>
                            <Button
                                as="a"
                                href="https://chrome.google.com/webstore"
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="secondary"
                                pill
                                size="xl"
                                icon={Download}
                                className="bg-white text-brand-800 hover:bg-brand-50 shadow-lg hover:shadow-xl hover:scale-105 transition-all border-none"
                            >
                                Install Gift Scout
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
